// carSheets/orphanedIncomeUtils.js
import { db } from "../../../firebase/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
} from "firebase/firestore";

function cleanKey(value) {
  return String(value || "na")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 90) || "na";
}

function firstValue(...values) {
  return values.find((v) => v !== undefined && v !== null && String(v).trim() !== "") || "";
}

function makeOrphanedDocId(type, data = {}) {
  const original = data.originalData || {};

  const driverId = firstValue(data.driverId, original.driverId, original.driver_id, original.driverIdentifier);
  const platform = firstValue(data.platform, original.platform);
  const week = firstValue(data.week, data.weekKey, original.week, original.weekKey);
  const date = firstValue(data.date, data.tripDate, original.date, original.tripDate, original.pickupDate, original.batchDate);
  const uploadBatchId = firstValue(data.uploadBatchId, original.uploadBatchId, original.batchId);
  const sourceDriverId = firstValue(data.sourceDriverId, original.sourceDriverId, original.driverIdentifier);
  const tripId = firstValue(original.tripId, original.id, original.rideId, original.bookingId);
  const amount = firstValue(data.amount, original.amount, original.driverFinalPrice);

  const uniqueTail = firstValue(uploadBatchId, tripId, sourceDriverId, amount, "orphan");
  const periodKey = firstValue(week, date, "no_period");

  return [type, driverId, platform, periodKey, uniqueTail]
    .map(cleanKey)
    .join("__")
    .slice(0, 500);
}

/**
 * Save orphaned income to Firestore.
 *
 * Important safety behavior:
 * - Uses a deterministic document id, so rebuilding the same month does not create duplicates.
 * - Does not overwrite an existing assigned/ignored status back to pending.
 */
export async function saveOrphanedIncome(month, type, data) {
  try {
    console.log(`💾 Saving orphaned ${type} to Firestore:`, data.driverName, data.amount);

    const colRef = collection(db, "orphanedIncome", month, type);
    const orphanId = makeOrphanedDocId(type, data);
    const docRef = doc(colRef, orphanId);
    const existingSnap = await getDoc(docRef);
    const existing = existingSnap.exists() ? existingSnap.data() : null;

    const docData = {
      ...data,
      month,
      type,
      createdAt: existing?.createdAt || new Date(),
      lastSeenAt: new Date(),
      status: existing?.status || data.status || "pending", // pending, assigned, ignored
      processed: existing?.processed || false,
    };

    if (existing?.assignedToCar) docData.assignedToCar = existing.assignedToCar;
    if (existing?.assignedAt) docData.assignedAt = existing.assignedAt;
    if (existing?.ignoredAt) docData.ignoredAt = existing.ignoredAt;

    await setDoc(docRef, docData, { merge: true });
    console.log(`✅ Saved/updated orphaned ${type} with ID: ${orphanId}`);

    return orphanId;
  } catch (error) {
    console.error(`❌ Failed to save orphaned ${type}:`, error);
    return null;
  }
}

function shouldIncludeByStatus(status, includePending, includeAssigned) {
  const safeStatus = status || "pending";
  if (includePending && safeStatus === "pending") return true;
  if (includeAssigned && safeStatus === "assigned") return true;
  return false;
}

/**
 * Load orphaned income from Firestore.
 *
 * Defaults are kept for the Orphaned Income Manager page:
 * - pending rows are shown there so they can be assigned/ignored.
 *
 * Car sheet rebuild should call:
 * loadOrphanedIncome(month, { includePending: false, includeAssigned: true })
 * so pending orphaned records remain warnings only and do not duplicate income.
 */
export async function loadOrphanedIncome(month, options = {}) {
  const { includeAssigned = false, includePending = true } = options;

  try {
    console.log(`📥 Loading orphaned income for ${month}`);

    const weeklyRef = collection(db, "orphanedIncome", month, "weeklyEarnings");
    const weeklySnap = await getDocs(weeklyRef);
    const weekly = weeklySnap.docs
      .filter((docSnap) => shouldIncludeByStatus(docSnap.data().status, includePending, includeAssigned))
      .map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
        type: "weeklyEarnings",
        isOrphaned: true,
      }));

    const manualRef = collection(db, "orphanedIncome", month, "manualTrips");
    const manualSnap = await getDocs(manualRef);
    const manual = manualSnap.docs
      .filter((docSnap) => shouldIncludeByStatus(docSnap.data().status, includePending, includeAssigned))
      .map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
        type: "manualTrips",
        isOrphaned: true,
      }));

    console.log(`✅ Loaded: ${weekly.length} weekly, ${manual.length} manual`);
    return { weekly, manual };
  } catch (error) {
    console.error(`❌ Failed to load orphaned income:`, error);
    return { weekly: [], manual: [] };
  }
}

/**
 * Mark orphaned income as assigned
 */
export async function markOrphanedAsAssigned(month, type, docId, carNumber) {
  try {
    const docRef = doc(db, "orphanedIncome", month, type, docId);
    await updateDoc(docRef, {
      status: "assigned",
      assignedToCar: carNumber,
      assignedAt: new Date(),
      processed: true,
    });
    console.log(`✅ Marked ${docId} as assigned to ${carNumber}`);
  } catch (error) {
    console.error(`❌ Failed to mark as assigned:`, error);
  }
}
